import React, { useState, useEffect, useRef } from "react";
import {
  FiChevronsUp,
  FiChevronsDown,
  FiChevronDown
} from "react-icons/fi";
import "./FilterableHeader.css";


function FilterableHeader({
  column,
  displayName,
  data,
  selectedValues,
  onFilterChange,
  onSort,
  sortColumn,
  sortOrder,
  isFilterable = true
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  /* ──────────────────────────────────────────────────────
   * Build list of unique values for this column
   * (null / undefined become "N/A")
   * ──────────────────────────────────────────────────── */
  const uniqueValues = React.useMemo(
    () =>
      [...new Set(data.map(item => item[column] || "N/A"))].sort(
        (a, b) => a.localeCompare(b)
      ),
    [data, column]
  );

  const filteredValues = uniqueValues.filter(value =>
    value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  /* Close dropdown when clicking outside */
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ─────────── Filter handlers ─────────── */

  const isAllSelected =
    (selectedValues[column] || []).length === uniqueValues.length;

  function handleSelectAll(checked) {
    onFilterChange(column, checked ? uniqueValues : []);
  }

  function handleValueToggle(value, checked) {
    const current = selectedValues[column] || [];
    const next = checked
      ? [...current, value]
      : current.filter(v => v !== value);
    onFilterChange(column, next);
  }

  const hasActiveFilter =
    selectedValues[column] &&
    selectedValues[column].length > 0 &&
    selectedValues[column].length < uniqueValues.length;

  /* ─────────── Render ─────────── */

  return (
    <th className="filterable-header" ref={dropdownRef}>
      {/* header line */}
      <div className="header-content">
        <span
          className="header-label"
          style={{ cursor: "pointer", flex: 1 }}
          onClick={() => onSort(column)}
        >
          {displayName}
          {hasActiveFilter && <span className="filter-indicator">●</span>}
          <FiChevronsUp
            className={`sort-icon ${
              sortColumn === column && sortOrder === "asc" ? "active" : ""
            }`}
          />
          <FiChevronsDown
            className={`sort-icon ${
              sortColumn === column && sortOrder === "desc" ? "active" : ""
            }`}
          />
        </span>

        {isFilterable && (
          <FiChevronDown
            className={`filter-icon ${isOpen ? "open" : ""}`}
            onClick={e => {
              e.stopPropagation();
              setIsOpen(prev => !prev);
            }}
          />
        )}
      </div>

      {/* dropdown */}
      {isFilterable && isOpen && (
        <div
          className="filter-dropdown"
          onClick={e => e.stopPropagation()}
        >
          {/* search */}
          <input
            type="text"
            placeholder="Search…"
            className="filter-search"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            autoFocus
          />

          {/* scrollable list */}
          <div className="filter-options">
            {/* select-all checkbox */}
            <label className="filter-option">
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={e => handleSelectAll(e.target.checked)}
              />
              <span>Select All</span>
            </label>

            <div className="filter-divider" />

            {filteredValues.map(value => (
              <label key={value} className="filter-option">
                <input
                  type="checkbox"
                  checked={
                    selectedValues[column]?.includes(value) || false
                  }
                  onChange={e =>
                    handleValueToggle(value, e.target.checked)
                  }
                />
                <span>{value}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </th>
  );
}

export default FilterableHeader;
